import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { LeadDetailPanel } from "@/components/crm/LeadDetailPanel";
import { ENTITY_TYPES, LEAD_STATUSES, SOURCES, STATUS_CLASSES, STATUS_LABELS } from "@/lib/crmConstants";
import { Plus, Upload, Download, Search, RefreshCw } from "lucide-react";

const inputCls = "bg-black/30 border-white/15 text-white placeholder:text-white/30 rounded-xl focus-visible:ring-[color:var(--hs-focus)]";

export const LeadsTab = () => {
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [entityFilter, setEntityFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [selectedLead, setSelectedLead] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter !== "all") params.status = statusFilter;
      if (entityFilter !== "all") params.entity_type = entityFilter;
      if (sourceFilter !== "all") params.source = sourceFilter;
      const { data } = await api.get("/leads", { params });
      setLeads(data.items);
      setTotal(data.total);
    } catch (e) {
      toast.error("Could not load leads");
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, entityFilter, sourceFilter]);

  useEffect(() => {
    const t = setTimeout(fetchLeads, search ? 350 : 0);
    return () => clearTimeout(t);
  }, [fetchLeads, search]);

  const exportCSV = async () => {
    try {
      const res = await fetch(`${API}/leads/export`, { credentials: "include" });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `holdstill-leads-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Leads exported — open the CSV directly in Google Sheets or Excel");
    } catch (e) {
      toast.error("Export failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="crm-leads-tab">
      {/* Toolbar */}
      <div className="flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
        <div className="flex flex-col sm:flex-row gap-2 flex-1">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30" />
            <Input
              data-testid="crm-leads-search-input"
              placeholder="Search name, email, phone…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={`${inputCls} pl-9 h-10`}
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className={`${inputCls} h-10 w-[130px]`} data-testid="crm-status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-[color:var(--hs-bg-2)] border-white/15 text-white">
                <SelectItem value="all">All statuses</SelectItem>
                {LEAD_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={entityFilter} onValueChange={setEntityFilter}>
              <SelectTrigger className={`${inputCls} h-10 w-[150px]`} data-testid="crm-entity-filter">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent className="bg-[color:var(--hs-bg-2)] border-white/15 text-white max-h-64">
                <SelectItem value="all">All types</SelectItem>
                {ENTITY_TYPES.map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={sourceFilter} onValueChange={setSourceFilter}>
              <SelectTrigger className={`${inputCls} h-10 w-[120px]`} data-testid="crm-source-filter">
                <SelectValue placeholder="Source" />
              </SelectTrigger>
              <SelectContent className="bg-[color:var(--hs-bg-2)] border-white/15 text-white">
                <SelectItem value="all">All sources</SelectItem>
                {SOURCES.map((s) => (
                  <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchLeads} data-testid="crm-refresh-leads-button" className="h-10 bg-transparent border-white/15 text-white/70 hover:bg-white/5 hover:text-white rounded-xl">
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="crm-csv-import-button" className="h-10 bg-transparent border-white/15 text-white/80 hover:bg-white/5 hover:text-white rounded-xl">
            <Upload className="h-4 w-4 mr-2" /> Import
          </Button>
          <Button variant="outline" onClick={exportCSV} data-testid="crm-csv-export-button" className="h-10 bg-transparent border-white/15 text-white/80 hover:bg-white/5 hover:text-white rounded-xl">
            <Download className="h-4 w-4 mr-2" /> Export
          </Button>
          <Button onClick={() => setAddOpen(true)} data-testid="crm-add-lead-button" className="h-10 bg-[color:var(--hs-lime)] text-[color:var(--hs-bg-0)] hover:bg-[color:var(--hs-lime-2)] font-semibold rounded-xl btn-lift">
            <Plus className="h-4 w-4 mr-2" /> Add Lead
          </Button>
        </div>
      </div>

      <p className="font-mono-ui text-[11px] text-white/40 uppercase tracking-[0.2em]">{total} lead{total === 1 ? "" : "s"}</p>

      {/* Table */}
      <div className="rounded-2xl border border-white/10 bg-[color:var(--hs-bg-1)] overflow-hidden">
        {loading ? (
          <div className="p-4 space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-12 rounded-xl bg-white/5" />
            ))}
          </div>
        ) : leads.length === 0 ? (
          <div className="p-10 text-center">
            <p className="text-white/50 text-sm">No leads match. New website consult requests land here automatically.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="crm-leads-table">
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="text-white/40 text-xs uppercase tracking-wider">Name</TableHead>
                  <TableHead className="text-white/40 text-xs uppercase tracking-wider">Contact</TableHead>
                  <TableHead className="text-white/40 text-xs uppercase tracking-wider">Type</TableHead>
                  <TableHead className="text-white/40 text-xs uppercase tracking-wider">Source</TableHead>
                  <TableHead className="text-white/40 text-xs uppercase tracking-wider">Preferred slot</TableHead>
                  <TableHead className="text-white/40 text-xs uppercase tracking-wider">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leads.map((lead) => (
                  <TableRow
                    key={lead.id}
                    onClick={() => setSelectedLead(lead)}
                    data-testid={`crm-lead-row-${lead.id}`}
                    className="border-white/5 cursor-pointer hover:bg-white/[0.04]"
                  >
                    <TableCell className="text-white font-medium">{lead.name}</TableCell>
                    <TableCell className="text-white/60 text-sm">
                      <div className="max-w-[200px] truncate">{lead.email}</div>
                      <div className="text-xs text-white/40">{lead.phone}</div>
                    </TableCell>
                    <TableCell className="text-white/60 text-sm">{lead.entity_type}</TableCell>
                    <TableCell>
                      <span className="font-mono-ui text-[10px] uppercase tracking-wider text-white/50">{lead.source}</span>
                    </TableCell>
                    <TableCell className="text-white/60 text-sm whitespace-nowrap">
                      {lead.preferred_date ? `${lead.preferred_date} · ${lead.preferred_time}` : "—"}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${STATUS_CLASSES[lead.status] || STATUS_CLASSES.new}`}>
                        {STATUS_LABELS[lead.status] || lead.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <LeadDetailPanel lead={selectedLead} onClose={() => setSelectedLead(null)} onUpdated={fetchLeads} />
      <AddLeadDialog open={addOpen} onOpenChange={setAddOpen} onCreated={fetchLeads} />
      <ImportDialog open={importOpen} onOpenChange={setImportOpen} onImported={fetchLeads} />
    </div>
  );
};

const AddLeadDialog = ({ open, onOpenChange, onCreated }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", entity_type: "Other", links: "", feedback: "", status: "new" });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/leads", { ...form, source: "manual" });
      toast.success("Lead added");
      onOpenChange(false);
      setForm({ name: "", email: "", phone: "", entity_type: "Other", links: "", feedback: "", status: "new" });
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add lead");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[color:var(--hs-bg-1)] border-white/15 text-white sm:max-w-lg rounded-2xl" data-testid="crm-add-lead-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase text-xl">Add lead</DialogTitle>
          <DialogDescription className="text-white/50">Manually add a custom lead to the pipeline.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input data-testid="add-lead-name-input" placeholder="Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={`${inputCls} h-11`} />
          <div className="grid grid-cols-2 gap-3">
            <Input data-testid="add-lead-email-input" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={`${inputCls} h-11`} />
            <Input data-testid="add-lead-phone-input" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={`${inputCls} h-11`} />
          </div>
          <Select value={form.entity_type} onValueChange={(v) => setForm({ ...form, entity_type: v })}>
            <SelectTrigger className={`${inputCls} h-11`} data-testid="add-lead-entity-select">
              <SelectValue placeholder="Entity type" />
            </SelectTrigger>
            <SelectContent className="bg-[color:var(--hs-bg-2)] border-white/15 text-white max-h-64">
              {ENTITY_TYPES.map((t) => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input data-testid="add-lead-links-input" placeholder="Profile / social links" value={form.links} onChange={(e) => setForm({ ...form, links: e.target.value })} className={`${inputCls} h-11`} />
          <Textarea data-testid="add-lead-notes-input" placeholder="Notes (optional)" rows={2} value={form.feedback} onChange={(e) => setForm({ ...form, feedback: e.target.value })} className={`${inputCls} resize-none`} />
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} data-testid="add-lead-save-button" className="bg-[color:var(--hs-lime)] text-[color:var(--hs-bg-0)] hover:bg-[color:var(--hs-lime-2)] font-semibold rounded-xl h-11 w-full sm:w-auto">
            {saving ? "Saving…" : "Add Lead"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ImportDialog = ({ open, onOpenChange, onImported }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) {
      toast.error("Choose a CSV or Excel file first");
      return;
    }
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/leads/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`Imported ${data.imported} leads (${data.skipped} skipped)`);
      onImported();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[color:var(--hs-bg-1)] border-white/15 text-white sm:max-w-md rounded-2xl" data-testid="crm-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase text-xl">Import leads</DialogTitle>
          <DialogDescription className="text-white/50">
            Upload a CSV or Excel (.xlsx) file. Recognised columns: name, email, phone, entity type, links, status, notes.
          </DialogDescription>
        </DialogHeader>
        <input
          ref={fileRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          data-testid="crm-import-file-input"
          className="block w-full text-sm text-white/70 file:mr-4 file:rounded-xl file:border-0 file:bg-[color:var(--hs-lime)] file:px-4 file:py-2.5 file:text-sm file:font-semibold file:text-[color:var(--hs-bg-0)] hover:file:bg-[color:var(--hs-lime-2)] file:cursor-pointer cursor-pointer rounded-xl border border-dashed border-white/20 p-3 bg-black/20"
        />
        {result && (
          <p className="text-sm text-[color:var(--hs-lime)]" data-testid="crm-import-result">
            Imported {result.imported} · Skipped {result.skipped}
          </p>
        )}
        <DialogFooter>
          <Button onClick={upload} disabled={uploading} data-testid="crm-import-upload-button" className="bg-[color:var(--hs-lime)] text-[color:var(--hs-bg-0)] hover:bg-[color:var(--hs-lime-2)] font-semibold rounded-xl h-11 w-full sm:w-auto">
            {uploading ? "Importing…" : "Import File"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
